import classNames from 'classnames';
import React, { FC, memo, ReactNode, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getLoggedUserData } from 'entities/User';
import { MenuItem } from '../model/types';
import { Menu } from 'shared/ui/Menu';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { ReactComponent as BookingIcon } from 'shared/assets/icons/newIcons/BookingIcon.svg';
import { getGlobalSettings } from 'app/appState';

interface HeaderProps {
  logo?: ReactNode;
  actions?: ReactNode[];
  menuItems?: MenuItem[];
  headerContent?: ReactNode;
  className?: string;
}

export const Header: FC<HeaderProps> = memo((props) => {
  const { className, actions, logo, menuItems = [], headerContent } = props;
  const { pathname } = useLocation();

  const activeRoute = menuItems.find(({ label }) => {
    if (React.isValidElement(label)) {
      return label.props.to === pathname;
    }
    return null;
  });

  const loggedInUser = useAppSelector(getLoggedUserData);
  const loggedUserRole = loggedInUser?.role;
  const globalSettings = useAppSelector(getGlobalSettings);

  const homeUrl = loggedInUser ? AppRoutes.HOME : globalSettings?.landingUrl || AppRoutes.HOME;

  const { isDesktop } = useScreenBreakpoints();

  const getAllowedRoutes = useCallback((): MenuItem[] => {
    if (loggedUserRole) {
      return menuItems.filter((item) => {
        const hasAccess = item.allowedroles?.length ? item.allowedroles.includes(loggedUserRole) : true;
        return hasAccess;
      });
    }
    return [];
  }, [loggedUserRole, menuItems]);

  const renderHeaderContent = (): ReactNode => {
    if (menuItems.length && !headerContent) {
      return <Menu className="flex flex-1 border-none" items={getAllowedRoutes()} activeKey={String(activeRoute?.key)} />;
    }

    if (Boolean(headerContent) && !menuItems.length) {
      return headerContent;
    }

    return null;
  };

  return (
    <>
      {isDesktop ? (
        <div className={classNames('flex items-center justify-between py-2 px-6 bg-white rounded-lg', className)}>
          <div className="mr-5">
            <Link to={homeUrl}>{logo}</Link>
          </div>
          {renderHeaderContent()}
          <div className="flex items-center justify-between space-x-3 ml-5">{actions}</div>
        </div>
      ) : (
        <div className={classNames('flex items-center justify-center w-full py-4 bg-backgroundColor relative', className)}>
          <Link to={AppRoutes.BOOKING} className="bg-trasparent p-2 rounded-lg absolute left-0 top-2.5 border-1.5 border-primary">
            <BookingIcon className="w-[24px] h-[24px]" />
          </Link>
          <Link to={homeUrl}>{logo}</Link>
          <div className="absolute right-0 top-2.5 flex items-center space-x-3">{actions}</div>
        </div>
      )}
    </>
  );
});
